import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatTL, STATUS_LABELS, PRIORITY_LABELS, formatDate } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { ClipboardList, Wallet, CheckCircle2, Clock, ArrowRight, Megaphone } from "lucide-react";

export default function MyJobs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [news, setNews] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/jobs"), api.get("/dashboard/tech")])
      .then(([j, s]) => { setJobs(j.data); setStats(s.data); });
    api.get("/announcements/active").then((r) => setNews(r.data)).catch(() => {});
  }, []);

  const open = jobs.filter(j => ["atandi", "yolda", "basladi"].includes(j.status));
  const completed = jobs.filter(j => ["tamamlandi", "faturalandi"].includes(j.status));

  const JobCard = ({ j }) => (
    <Link to={`/tech/jobs/${j.id}`} className="block" data-testid={`tech-job-${j.id}`}>
      <Card className="p-4 border border-slate-200 shadow-none card-lift">
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="font-mono text-xs text-blue-700 font-bold">{j.job_number}</div>
            <div className="font-display font-semibold text-slate-900 mt-0.5">{j.customer_name}</div>
          </div>
          <span className={`status-pill border ${STATUS_LABELS[j.status]?.color}`}>{STATUS_LABELS[j.status]?.label}</span>
        </div>
        <div className="text-sm text-slate-600 line-clamp-2">{j.fault_description}</div>
        <div className="mt-3 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            {j.device_brand_logo ? (
              <img src={j.device_brand_logo} alt="" className="h-5 w-5 object-contain" />
            ) : null}
            <span className="text-slate-500">{j.device_brand_name || "Cihaz —"}</span>
            {j.priority !== "normal" && <span className={`status-pill ${PRIORITY_LABELS[j.priority]?.color}`}>{PRIORITY_LABELS[j.priority]?.label}</span>}
          </div>
          <ArrowRight className="h-4 w-4 text-slate-400" />
        </div>
        <div className="mt-2 text-xs text-slate-400">{j.customer_phone} • {formatDate(j.created_at)}</div>
      </Card>
    </Link>
  );

  return (
    <div className="space-y-5" data-testid="tech-myjobs">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Hoş geldin</div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-1">{user?.name}</h1>
            {user?.commission_percent != null && (
              <div className="text-xs text-slate-500 mt-1">Şirkete borç oranınız: <span className="font-mono font-bold text-amber-700">%{user.commission_percent}</span></div>
            )}
          </div>

      {stats && (
        <div className="grid grid-cols-3 gap-2">
          <Card className="p-3 border border-slate-200 shadow-none">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 font-bold"><Clock className="h-3 w-3" /> Açık</div>
            <div className="font-display text-2xl font-bold text-slate-900 mt-1">{stats.open_jobs}</div>
          </Card>
          <Card className="p-3 border border-slate-200 shadow-none">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 font-bold"><CheckCircle2 className="h-3 w-3" /> Bugün</div>
            <div className="font-display text-2xl font-bold text-emerald-700 mt-1">{stats.completed_today}</div>
          </Card>
          <Card className="p-3 border border-slate-200 shadow-none">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 font-bold"><Wallet className="h-3 w-3" /> Borç</div>
            <div className="font-mono text-base font-bold text-rose-700 mt-1">{formatTL(stats.balance)}</div>
          </Card>
        </div>
      )}

      <Link to="/tech/diagnosis" className="block text-center text-sm font-semibold text-blue-700 border border-blue-200 bg-blue-50 rounded-lg py-2" data-testid="home-diag">Arıza Asistanı</Link>
      {news.slice(0, 2).map((a) => (
        <div key={a.id} data-testid={`tech-announcement-${a.id}`} className={`flex items-start gap-3 p-3.5 rounded-xl border text-sm ${a.level === "onemli" ? "bg-rose-50 border-rose-200 text-rose-900" : a.level === "uyari" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-blue-50 border-blue-200 text-blue-900"}`}>
          <Megaphone className="h-4 w-4 mt-0.5 shrink-0" />
          <div><Link to="/tech/announcements" className="font-semibold underline-offset-2 hover:underline">{a.title}</Link>{a.body && <div className="mt-0.5 opacity-90 whitespace-pre-wrap">{a.body}</div>}</div>
        </div>
      ))}

      <Tabs defaultValue="open">
        <TabsList className="w-full grid grid-cols-2">
          <TabsTrigger value="open">Aktif Görevler ({open.length})</TabsTrigger>
          <TabsTrigger value="done">Tamamlanan ({completed.length}) · 7 gün</TabsTrigger>
        </TabsList>
        <TabsContent value="open" className="space-y-3 mt-4">
          {open.length === 0 ? (
            <Card className="p-10 text-center border border-slate-200 shadow-none">
              <ClipboardList className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <div className="text-slate-500 text-sm">Aktif göreviniz yok</div>
            </Card>
          ) : open.map(j => <JobCard key={j.id} j={j} />)}
        </TabsContent>
        <TabsContent value="done" className="space-y-3 mt-4">
          {completed.length === 0 ? (
            <Card className="p-10 text-center border border-slate-200 shadow-none">
              <CheckCircle2 className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <div className="text-slate-500 text-sm">Henüz tamamlanan görev yok</div>
            </Card>
          ) : completed.map(j => <JobCard key={j.id} j={j} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
